/**
 * Question bank validator / linter.
 *
 * Validates the structural integrity of question data:
 *  - Required fields present and non-empty
 *  - Correct answer references a valid choice id
 *  - Exactly 4 choices with ids a/b/c/d
 *  - No duplicate question ids
 *  - Topic is a known QuestionTopic
 *  - Difficulty is a known QuestionDifficulty
 *  - linkedQualIds reference known qualification ids
 *
 * Usage (runtime): import { validateQuestions } from './validateQuestions';
 *   const errors = validateQuestions(questionBank);
 *   if (errors.length) console.error(errors);
 *
 * Usage (CLI):  npx tsx src/utils/validateQuestions.ts
 */

import { QUESTION_TOPICS } from '../types';
import type { Question } from '../types';

const VALID_TOPICS = new Set<string>(QUESTION_TOPICS.map((t) => t.id));
const VALID_DIFFICULTIES = new Set<string>(['basic', 'intermediate', 'advanced']);
const VALID_CHOICE_IDS = new Set(['a', 'b', 'c', 'd']);
const KNOWN_QUAL_IDS = new Set([
  'baq', 'electrical', 'hoist', 'bighoss',
  'lead_install', 'lead_diagnostic',
  'plumb_apprentice', 'plumb_master',
]);

export interface ValidationError {
  questionId: string;
  field: string;
  message: string;
}

export function validateQuestion(q: Question, seenIds: Set<string>): ValidationError[] {
  const errors: ValidationError[] = [];
  const err = (field: string, message: string) =>
    errors.push({ questionId: q.id ?? '(missing)', field, message });

  // id
  if (!q.id || typeof q.id !== 'string') {
    err('id', 'Missing or non-string id');
  } else if (seenIds.has(q.id)) {
    err('id', `Duplicate question id "${q.id}"`);
  }

  // topic
  if (!q.topic) {
    err('topic', 'Missing topic');
  } else if (!VALID_TOPICS.has(q.topic)) {
    err('topic', `Unknown topic "${q.topic}". Valid: ${[...VALID_TOPICS].join(', ')}`);
  }

  // difficulty
  if (!q.difficulty) {
    err('difficulty', 'Missing difficulty');
  } else if (!VALID_DIFFICULTIES.has(q.difficulty)) {
    err('difficulty', `Unknown difficulty "${q.difficulty}"`);
  }

  // question text
  if (!q.question || typeof q.question !== 'string' || q.question.trim().length === 0) {
    err('question', 'Missing or empty question text');
  }

  // choices
  if (!Array.isArray(q.choices)) {
    err('choices', 'Choices must be an array');
  } else {
    if (q.choices.length !== 4) {
      err('choices', `Expected 4 choices, got ${q.choices.length}`);
    }
    const choiceIds = new Set(q.choices.map((c) => c.id));
    for (const expected of VALID_CHOICE_IDS) {
      if (!choiceIds.has(expected)) {
        err('choices', `Missing choice "${expected}"`);
      }
    }
    for (const c of q.choices) {
      if (!c.text || typeof c.text !== 'string' || c.text.trim().length === 0) {
        err(`choices.${c.id}`, 'Empty choice text');
      }
    }
  }

  // correctAnswer
  if (!q.correctAnswer) {
    err('correctAnswer', 'Missing correctAnswer');
  } else if (!VALID_CHOICE_IDS.has(q.correctAnswer)) {
    err('correctAnswer', `correctAnswer "${q.correctAnswer}" is not a valid choice id`);
  } else if (Array.isArray(q.choices) && !q.choices.find((c) => c.id === q.correctAnswer)) {
    err('correctAnswer', `correctAnswer "${q.correctAnswer}" does not match any choice`);
  }

  // explanation
  if (!q.explanation || typeof q.explanation !== 'string' || q.explanation.trim().length === 0) {
    err('explanation', 'Missing or empty explanation');
  }

  // isMath
  if (typeof q.isMath !== 'boolean') {
    err('isMath', 'isMath must be a boolean');
  }

  // linkedQualIds
  if (!Array.isArray(q.linkedQualIds)) {
    err('linkedQualIds', 'linkedQualIds must be an array');
  } else {
    if (q.linkedQualIds.length === 0) {
      err('linkedQualIds', 'linkedQualIds is empty — every question should link to at least one qualification');
    }
    for (const qid of q.linkedQualIds) {
      if (!KNOWN_QUAL_IDS.has(qid)) {
        err('linkedQualIds', `Unknown qualification id "${qid}"`);
      }
    }
  }

  return errors;
}

export function validateQuestions(questions: Question[]): ValidationError[] {
  const seenIds = new Set<string>();
  const allErrors: ValidationError[] = [];

  for (const q of questions) {
    const errors = validateQuestion(q, seenIds);
    allErrors.push(...errors);
    if (q.id) seenIds.add(q.id);
  }

  return allErrors;
}

/** Summary stats for a question bank */
export function questionBankStats(questions: Question[]) {
  const byTopic: Record<string, number> = {};
  const byDifficulty: Record<string, number> = {};
  const byQual: Record<string, number> = {};

  for (const q of questions) {
    byTopic[q.topic] = (byTopic[q.topic] || 0) + 1;
    byDifficulty[q.difficulty] = (byDifficulty[q.difficulty] || 0) + 1;
    for (const qid of q.linkedQualIds) {
      byQual[qid] = (byQual[qid] || 0) + 1;
    }
  }

  return { total: questions.length, byTopic, byDifficulty, byQual };
}

